/**
 * ReportWhatIf — "Mitä jos?" section. The questions entered in the Mitä jos
 * module (metadata.hmw_questions, the `questions` prop) are the primary
 * source: one card per question, the text read-only here (#1184). Without
 * them, 10 facilitator-editable question slots, each with image placeholder
 * + alt-text + question text.
 *
 * MVP (V3.2): empty slots that fasilitaattori fills in via the admin
 * EditableText overlay. AI-visualisation deferred to v2 per käsikirjoitus
 * 2026-05 ("Alkuvaiheessa voi olla vain kuvapaikkoja").
 *
 * Image URLs stored in field_edits under whatif.{n}.image_url. For now
 * just plain text URLs — file upload UI is a separate concern.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Image as ImageIcon } from 'lucide-react';
import { Section, SectionTitle, Paragraph, SITRA } from './reportStyles';
import WorkshopAction from './WorkshopAction';
import EditableText from './EditableText';
import ImageLightbox from './ImageLightbox';
import { useReportEdits } from './ReportEditsContext';
import { normalizeImageUrl } from './imageUrl';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const SLOT_COUNT = 10;

/** Module questions as trimmed strings; readers also accept older { text } / { question } entries. */
function moduleQuestionsOf(questions) {
  if (!Array.isArray(questions)) return [];
  return questions
    .map((q) => (typeof q === 'string' ? q : q?.text || q?.question || ''))
    .map((q) => q.trim())
    .filter(Boolean);
}

const Intro = styled(Paragraph)`
  margin-bottom: 12px;
`;

const SubHint = styled.div`
  font-size: 0.8rem;
  color: ${SITRA.textMuted};
  font-style: italic;
  margin-bottom: 32px;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-top: 24px;
  width: 100%;
`;

const Card = styled.figure`
  margin: 0;
  background: ${SITRA.white};
  border: 1px solid ${SITRA.border};
  display: flex;
  flex-direction: column;
  flex: 1 1 280px;
  min-width: 0;
  max-width: 100%;
`;

const ImageBox = styled.div`
  background: ${SITRA.surfaceMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${SITRA.textMuted};
  /* No forced aspect-ratio — KUVAT images are uniform, container hugs them. */
  img {
    display: block;
    width: 100%;
    height: auto;
    cursor: zoom-in;
  }
`;

/* Empty slots still need a fallback sized box; otherwise the placeholder
 * has zero height. Only applies when there's no image URL. */
const PlaceholderImageBox = styled(ImageBox)`
  aspect-ratio: 4 / 3;
`;

/* A slot with a question but no picture. The question is the content — the
 * illustrations were always decoration around it — so the card keeps the
 * image's footprint and sets the text on a dark panel instead of rendering
 * nothing. Same aspect-ratio as PlaceholderImageBox so a mixed row of
 * illustrated and text-only slots still lines up.
 *
 * Colours cycle through the Sitra brand darks rather than repeating one: ten
 * identical panels read as a table, and these are meant to be read as ten
 * separate provocations. All four carry white text at well past 4.5:1. */
const TEXT_CARD_COLORS = [SITRA.green, SITRA.blue, SITRA.amber, SITRA.puolukka];

const TextBox = styled.div`
  background: ${props => props.color};
  color: ${SITRA.white};
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  padding: 28px 26px;
  font-size: 1.05rem;
  line-height: 1.45;
  font-weight: 500;
  /* The longest question is ~230 characters; without this the text overflows
     a narrow card instead of shrinking to fit it. */
  container-type: inline-size;

  @container (max-width: 320px) {
    font-size: 0.95rem;
    padding: 20px 18px;
  }
`;

/* The lightbox itself lives in `ImageLightbox.jsx` now — shared with the
   `tulevaisuuskuvat` slot in PublicWorkshopReport. The styled wrappers
   + keyframes that used to live here moved with it. */

const PlaceholderHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  text-align: center;
  padding: 16px;
`;

const Caption = styled.figcaption`
  padding: 16px 18px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${SITRA.textBody};
  border-top: 1px solid ${SITRA.border};
  min-height: 80px;
`;

const SlotNum = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${SITRA.blue};
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  text-transform: uppercase;
`;

const ParticipantSlot = styled.div`
  margin-top: 40px;
  padding: 20px 24px;
  background: ${SITRA.surfaceMuted};
  border-left: 4px solid ${SITRA.blue};
`;

const ParticipantTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${SITRA.black};
  margin: 0 0 8px 0;
`;

/**
 * Single slot. If image_url is empty, shows placeholder hint.
 * If admin in edit mode, the EditableText shows pen icons.
 * moduleQuestion, when given, replaces the slot's own question text.
 */
function WhatIfSlot({ index, moduleQuestion, onImageClick, t }) {
  const { get, editMode } = useReportEdits();
  const imageUrl = normalizeImageUrl(get(`whatif.${index}.image_url`, ''));
  const altText = get(`whatif.${index}.alt`, '');
  const defaultAlt = t('report.whatIf.imageAlt', { index: index + 1 });

  // Non-edit mode: image only. All meta (slot number, question text,
  // URL line, alt-text line) is admin/edit-time furniture that doesn't
  // belong in the public report. Empty slots render nothing — no point
  // showing public viewers a placeholder for an unfilled slot.
  // Click image → lightbox.
  if (!editMode) {
    if (!imageUrl) {
      // No picture, but the question itself is publishable content.
      const question = moduleQuestion ?? get(`whatif.${index}.question`, '').trim();
      if (!question) return null;
      return (
        <Card>
          <TextBox color={TEXT_CARD_COLORS[index % TEXT_CARD_COLORS.length]}>
            {question}
          </TextBox>
        </Card>
      );
    }
    return (
      <Card>
        <ImageBox>
          <img
            src={imageUrl}
            alt={altText || defaultAlt}
            onClick={() => onImageClick({ url: imageUrl, alt: altText || defaultAlt })}
          />
        </ImageBox>
      </Card>
    );
  }

  // Edit mode: full meta panel for fasilitaattori to edit URL + alt + question.
  return (
    <Card>
      {imageUrl
        ? <ImageBox><img
            src={imageUrl}
            alt={altText || defaultAlt}
            onClick={() => onImageClick({ url: imageUrl, alt: altText || defaultAlt })}
          /></ImageBox>
        : <PlaceholderImageBox>
            <PlaceholderHint>
              <ImageIcon size={32} strokeWidth={1.2} />
              <div>{t('report.whatIf.placeholder')}</div>
            </PlaceholderHint>
          </PlaceholderImageBox>}
      <Caption>
        <SlotNum>{t('report.whatIf.title')} #{index + 1}</SlotNum>
        {moduleQuestion !== undefined
          ? <>
              <div>{moduleQuestion}</div>
              <div style={{ marginTop: 6, fontSize: '0.75rem', color: SITRA.textMuted }}>{t('report.whatIf.fromModule')}</div>
            </>
          : <EditableText
              fieldKey={`whatif.${index}.question`}
              fallback={`Lisää tähän Mitä jos -kysymys ${index + 1}.`}
              label={`Mitä jos -kysymys ${index + 1}`}
            />}
        <div style={{ marginTop: 10, fontSize: '0.75rem', color: SITRA.textMuted }}>
          <strong>{t('report.whatIf.imageUrl')}:</strong>{' '}
          <EditableText
            fieldKey={`whatif.${index}.image_url`}
            fallback="—"
            label={`Kuva-URL (slot ${index + 1})`}
            multiline={false}
          />
          {' · '}
          <strong>{t('report.whatIf.altText')}:</strong>{' '}
          <EditableText
            fieldKey={`whatif.${index}.alt`}
            fallback="—"
            label={`Alt-teksti (slot ${index + 1})`}
            multiline={false}
          />
        </div>
      </Caption>
    </Card>
  );
}

export default function ReportWhatIf({ questions, children }) {
  const { t } = useTranslation();
  const { get } = useReportEdits();
  const moduleQuestions = moduleQuestionsOf(questions);
  const slotCount = moduleQuestions.length || SLOT_COUNT;

  // Build the gallery from whatever slots actually have URLs. Index into
  // this array drives both the click → open and the lightbox's prev/next
  // navigation — so the chevrons only walk slots that actually exist.
  const gallery = [];
  for (let i = 0; i < slotCount; i++) {
    const url = normalizeImageUrl(get(`whatif.${i}.image_url`, ''));
    if (!url) continue;
    const alt = get(`whatif.${i}.alt`, t('report.whatIf.imageAlt', { index: i + 1 }));
    gallery.push({ url, alt });
  }

  // null = closed. Number = the index in `gallery` (NOT the slot index)
  // at which to open the lightbox.
  const [lightboxStart, setLightboxStart] = useState(null);

  // Map a slot-level click ({ url, alt } from WhatIfSlot) to the matching
  // index in our gallery, then open the lightbox there.
  const openLightboxForSlot = ({ url }) => {
    const idx = gallery.findIndex((g) => g.url === url);
    setLightboxStart(idx >= 0 ? idx : 0);
  };

  return (
    <Section id="mitajos">
      <SectionTitle>{t('report.whatIf.title')}</SectionTitle>
      <Intro>
        <EditableText
          fieldKey="phase.mitajos.intro"
          fallback={t('report.whatIf.introDefault')}
        />
      </Intro>
      <SubHint>
        {t('report.whatIf.creditsHint')}
      </SubHint>

      <WorkshopAction>
        {t('report.whatIf.workshopAction')}
      </WorkshopAction>

      <Grid>
        {Array.from({ length: slotCount }, (_, i) => (
          <WhatIfSlot key={i} index={i} moduleQuestion={moduleQuestions[i]} onImageClick={openLightboxForSlot} t={t} />
        ))}
      </Grid>

      {lightboxStart !== null && gallery.length > 0 && (
        <ImageLightbox
          images={gallery}
          initialIndex={lightboxStart}
          onClose={() => setLightboxStart(null)}
        />
      )}

      {/* Osallistujien itse nostamat kysymykset + äänitallenteet renderöityvät
          tämän alle, alkuperäisten Entä jos / Hiljaiset signaalit -transkriptien
          kautta (children prop tuo ne PublicWorkshopReport:sta). */}
      {children && (
        <ParticipantSlot>
          <ParticipantTitle>{t('report.whatIf.title')}</ParticipantTitle>
          {children}
        </ParticipantSlot>
      )}
    </Section>
  );
}
